"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { isExcluded, safeHeadingFor, whyHeadingFor, type AllergenPage } from "@/lib/allergen-pages";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";

export function AllergenDetail({
  page,
  allPages,
}: {
  page: AllergenPage;
  allPages: AllergenPage[];
}) {
  const otherPages = allPages.filter((p) => p.slug !== page.slug);
  const t = useTranslations("allergen");
  const tAllg = useTranslations("allergenPage");
  const tCommon = useTranslations("common");

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-8 lg:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">{tCommon("home")}</Link>
            <span>/</span>
            <Link href="/informatie" className="hover:text-foreground transition-colors">{tAllg("breadcrumb")}</Link>
            <span>/</span>
            <span className="text-text-secondary">{page.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12 lg:gap-16">
            {/* Main content */}
            <article>
              <p className="label-sm mb-3">{t("ingredientInfo")}</p>
              <h1 className="heading-lg mb-6 break-words hyphens-auto">{page.title}</h1>
              <p className="text-lg text-text-body leading-relaxed mb-10">
                {page.intro}
              </p>

              <div className="divider-bold mb-10" />

              <div className="space-y-10">
                <section>
                  <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">
                    {t("whatIsIt")}
                  </h2>
                  <p className="text-text-body text-[15px] leading-relaxed">
                    {page.explanation}
                  </p>
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">
                    {whyHeadingFor(page, t("whyExcluded"))}
                  </h2>
                  <p className="text-text-body text-[15px] leading-relaxed">
                    {page.whyExcluded}
                  </p>
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">
                    {t("whatWeUseInstead")}
                  </h2>
                  <p className="text-text-body text-[15px] leading-relaxed">
                    {page.whatWeUseInstead}
                  </p>
                </section>

                <section className="bg-surface p-6 lg:p-8 border border-border rounded-2xl">
                  <div className="flex items-start gap-3">
                    {isExcluded(page) ? (
                      <svg className="w-5 h-5 text-cta shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 8.5l3.5 3.5 6.5-8" />
                      </svg>
                    ) : (
                      <svg data-icon="info" className="w-5 h-5 text-text-secondary shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="8" cy="8" r="6.5" />
                        <path d="M8 7.25v4M8 4.75v.01" />
                      </svg>
                    )}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-2">
                        {safeHeadingFor(page, t("safeForYou"))}
                      </h3>
                      <p className="text-text-body text-[15px] leading-relaxed">
                        {page.safeForYou}
                      </p>
                    </div>
                  </div>
                </section>
              </div>

              {/* CTA */}
              <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row gap-3">
                <Link
                  href="/collecties/gezicht"
                  className="inline-flex items-center justify-center h-12 px-8 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors rounded-lg"
                  style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
                >
                  {t("viewProducts")}
                </Link>
                <a
                  href="https://wa.me/31657222000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-12 px-8 border border-foreground/20 text-foreground label-sm hover:border-foreground/40 transition-colors rounded-lg"
                  style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
                >
                  {t("personalAdvice")}
                </a>
              </div>
            </article>

            {/* Sidebar - other allergen pages */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="label-sm mb-4">{t("otherIngredients")}</h3>
                <nav className="space-y-1">
                  {otherPages.map((p) => (
                    <Link
                      key={p.slug}
                      href={`/informatie/${p.slug}`}
                      className="block text-sm text-text-secondary hover:text-foreground py-1.5 transition-colors"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                    >
                      {p.title}
                    </Link>
                  ))}
                </nav>

                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-xs text-text-tertiary leading-relaxed">
                    {tAllg("sidebarHelp")}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
